import { sessionFetch } from '../../../utils/sessionFetch';
import { useState, useEffect, useCallback } from "react";
import moment from 'moment';
import { getSubdomain, getServerEndpoint } from '../../../utils/utils';

const PAGE_SIZE = 20;

function DeletedPlantilles({ socket, autoShow = false, castellersInfo }) {
    const [deletedPlantilles, setDeletedPlantilles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDeletedPlantilles, setShowDeletedPlantilles] = useState(autoShow);
    const [limit, setLimit] = useState(PAGE_SIZE);

    const fetchDeletedPlantilles = useCallback(() => {
        const COLLA = getSubdomain();
        const serverEndpoint = getServerEndpoint(COLLA);

        sessionFetch(`${serverEndpoint}/api/deleted_plantilles`, {
            headers: { 'x-api-key': '453dabb4-7645-4626-a1bb-477dff3aa557' }
        })
            .then(res => res.json())
            .then(data => { setDeletedPlantilles(data); setLoading(false); })
            .catch(() => {});
    }, []);

    useEffect(() => {
        fetchDeletedPlantilles();

        const onDeletedPlantilla = () => fetchDeletedPlantilles();
        const onDeletedForeverPlantilla = () => fetchDeletedPlantilles();
        const onRestoredPlantilla = () => {
            fetchDeletedPlantilles();
            socket.emit('.request_plantilles');
        };
        socket.on('.deleted_plantilla', onDeletedPlantilla);
        socket.on('.deleted_forever_plantilla', onDeletedForeverPlantilla);
        socket.on('.restored_plantilla', onRestoredPlantilla);

        return () => {
            socket.off('.deleted_plantilla', onDeletedPlantilla);
            socket.off('.deleted_forever_plantilla', onDeletedForeverPlantilla);
            socket.off('.restored_plantilla', onRestoredPlantilla);
        }
    }, [fetchDeletedPlantilles]);

    const sortedPlantilles = [...deletedPlantilles].sort((a, b) => parseInt(b.timestamp) - parseInt(a.timestamp));

    // If autoShow is true, render directly without toggle buttons
    if (autoShow) {
        if (loading) return (
            <div className="editor-table-container" style={{ minHeight: '80vh' }}>
                <table className="editor-table">
                    <thead>
                        <tr>
                            <th>Nom</th>
                            <th>Eliminada</th>
                            <th>Eliminat per</th>
                            <th>Accions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Array.from({ length: 5 }, (_, i) => (
                            <tr key={i} className="skeleton-row">
                                <td><div className="skeleton-bar" style={{ width: '60%' }} /></td>
                                <td><div className="skeleton-bar" style={{ width: '50%' }} /></td>
                                <td><div className="skeleton-bar" style={{ width: '50%' }} /></td>
                                <td><div className="skeleton-bar" style={{ width: '40%' }} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );

        if (sortedPlantilles.length === 0) {
            return (
                <div className="editor-empty-state">
                    <div className="editor-empty-state-icon">&#128465;</div>
                    <p className="editor-empty-state-text">No hi ha plantilles eliminades</p>
                </div>
            );
        }

        return (
            <>
                <div className="editor-table-container">
                    <table className="editor-table">
                        <thead>
                            <tr>
                                <th>Nom</th>
                                <th>Eliminada</th>
                                <th>Eliminat per</th>
                                <th>Accions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedPlantilles.slice(0, limit).map(plantilla => {
                                const user = castellersInfo?.[plantilla.deletedBy];
                                return (
                                <tr key={plantilla.filename}>
                                    <td>{plantilla.nom}</td>
                                    <td className="editor-date-cell">
                                        {moment(parseInt(plantilla.timestamp)).format('DD/MM/YYYY HH:mm')}
                                    </td>
                                    <td>
                                        {user ? (user.mote || user.nom) : '—'}
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        <button
                                            className="editor-restore-btn"
                                            onClick={() => socket.emit('.restore_plantilla', plantilla.filename, plantilla.nom)}
                                        >
                                            Restaurar
                                        </button>
                                        {' '}
                                        <button
                                            className="editor-delete-forever-btn"
                                            onClick={() => {
                                                const confirmed = window.confirm(`Estàs segur que vols eliminar "${plantilla.nom}" per sempre? Aquesta acció no es pot desfer.`);
                                                if (confirmed) {
                                                    socket.emit('.delete_forever_plantilla', plantilla.filename);
                                                }
                                            }}
                                        >
                                            Eliminar per sempre
                                        </button>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {sortedPlantilles.length > limit && (
                    <button
                        className="editor-load-more-btn"
                        onClick={() => setLimit(prev => prev + PAGE_SIZE)}
                    >
                        Carregar més ({sortedPlantilles.length - limit} restants)
                    </button>
                )}
            </>
        );
    }

    return !showDeletedPlantilles ? (
        <div>
            <h3>Plantilles eliminades</h3>
            <button
                onClick={() => setShowDeletedPlantilles(true)}
            >
                Mostrar plantilles eliminades
            </button>
        </div>
    ) : (
        <div>
            <h3>Plantilles eliminades</h3>
            <button
                onClick={() => setShowDeletedPlantilles(false)}
            >
                Amagar plantilles eliminades
            </button>

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                }}
            >
                {
                    deletedPlantilles.map(plantilla => (
                        <div
                            key={plantilla.filename}
                            style={{
                                margin: 10,
                                padding: 10,
                                border: '1px solid black',
                                borderRadius: 5,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <div
                            >
                                <strong>{plantilla.nom}</strong>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    gap: 5,
                                }}
                            >
                                <span>
                                    Eliminada el
                                </span>
                                <span>
                                    {moment(parseInt(plantilla.timestamp)).format('DD/MM/YYYY HH:mm:ss')}
                                </span>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    gap: 15,
                                }}
                            >
                                <button
                                    onClick={() => {
                                        socket.emit('.restore_plantilla', plantilla.filename, plantilla.nom);
                                    }}
                                >
                                    Restaurar
                                </button>

                                <button
                                    onClick={() => {
                                        socket.emit('.delete_forever_plantilla', plantilla.filename);
                                    }}
                                    style={{
                                        backgroundColor: 'lightcoral',
                                    }}
                                >
                                    Eliminar per sempre
                                </button>
                            </div>
                        </div>
                    ))
                }
            </div>
        </div>
    );
}

export default DeletedPlantilles;